import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FaTh as Command, FaSignOutAlt as LogOut, FaCog as Settings, FaShieldAlt as Shield, FaMoon as Moon, FaSun as Sun, FaEyeSlash, FaEye } from 'react-icons/fa';
import { Plus } from 'lucide-react';
import { UserAvatar } from '../ui/user-avatar';
import { useAuth } from '../../context/AuthContext';
import { useDarkMode } from '../../context/DarkModeContext';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { useAppRegistry } from '../../hooks/useAppRegistry';
import { useCategoryOrder } from '../../hooks/useCategoryOrder';
import { useHiddenApps } from '../../hooks/useHiddenApps';
import { motion } from 'framer-motion';
import RandomGoodThing from '../good-things/RandomGoodThing';
import CategoryAppGrid from './CategoryAppGrid';
import { APP_CATEGORIES, APP_CATEGORY_LABELS } from '../../types/appRegistry';
import { setPageTitle } from '../../utils/pageTitle';
import { LoadingAnimation } from '../ui/loading-animation';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const AppHub: React.FC = () => {
  const navigate = useNavigate();
  const { userEmail, logout } = useAuth();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const { isAdmin } = useIsAdmin();
  const { apps, appsByCategory, loading, error, refresh } = useAppRegistry();
  const { getOrderForCategory, reorderInCategory, loading: orderLoading } = useCategoryOrder();
  const { hide, show, isHidden, loading: hiddenLoading } = useHiddenApps();
  const [showPLMSDialog, setShowPLMSDialog] = useState(false);
  const [showHidden, setShowHidden] = useState(false);

  const visibleAppsByCategory = useMemo(() => {
    const result: typeof appsByCategory = { core: [], productivity: [], community: [], experimental: [] };
    for (const cat of APP_CATEGORIES) {
      result[cat] = (appsByCategory[cat] || []).filter((a) => !isHidden(a.id));
    }
    return result;
  }, [appsByCategory, isHidden]);

  const hiddenApps = useMemo(
    () => apps.filter((a) => isHidden(a.id)),
    [apps, isHidden]
  );

  useEffect(() => {
    setPageTitle('Apps Hub');
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-gray-50 dark:bg-gray-900"
    >
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 border-b dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center mr-4">
              {React.createElement(Command as any, { className: 'h-8 w-8 text-blue-600' })}
              <h1 className="ml-3 text-2xl font-bold text-gray-900 dark:text-white hidden md:block">JLS Apps</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleDarkMode}
                className="p-2 text-gray-400 hover:text-gray-500 rounded-lg"
                title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
              >
                {isDarkMode
                  ? React.createElement(Sun as any, { className: 'h-5 w-5' })
                  : React.createElement(Moon as any, { className: 'h-5 w-5' })}
              </button>
              {isAdmin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="flex items-center gap-1 py-1.5 px-3 rounded-md bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm"
                >
                  {React.createElement(Shield as any, { className: 'h-3.5 w-3.5' })}
                  Admin
                </button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger className="cursor-pointer hover:opacity-80 transition-opacity">
                  <UserAvatar size="sm" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                    {userEmail}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
                    {React.createElement(Settings as any, { className: 'mr-2 h-4 w-4' })}
                    <span>Settings</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/submit-app')} className="cursor-pointer">
                    <Plus className="mr-2 h-4 w-4" />
                    <span>Submit an App</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} className="cursor-pointer">
                    {React.createElement(LogOut as any, { className: 'mr-2 h-4 w-4' })}
                    <span>Logout</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Show loading state */}
        {(loading || orderLoading || hiddenLoading) ? (
          <LoadingAnimation />
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500 dark:text-red-400 mb-4">Failed to load apps: {error}</p>
            <Button onClick={refresh} variant="outline">Retry</Button>
          </div>
        ) : (
          <>
            {/* Random Good Thing */}
            <RandomGoodThing />

            {/* Category-based app grids */}
            {APP_CATEGORIES.map(category => {
              const categoryApps = visibleAppsByCategory[category] || [];
              if (categoryApps.length === 0) return null;
              return (
                <CategoryAppGrid
                  key={category}
                  category={category}
                  apps={categoryApps}
                  orderedIds={getOrderForCategory(category)}
                  onReorder={(fromIndex, toIndex) => reorderInCategory(category, fromIndex, toIndex)}
                  onHideApp={hide}
                />
              );
            })}

            {/* Hidden Apps Section */}
            {hiddenApps.length > 0 && (
              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowHidden(!showHidden)}
                  className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                  {React.createElement(showHidden ? FaEye as any : FaEyeSlash as any, { className: 'h-4 w-4' })}
                  {showHidden ? 'Hide' : 'Show'} Hidden Apps ({hiddenApps.length})
                </button>
                {showHidden && (
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 opacity-60">
                    {hiddenApps.map((app) => (
                      <div key={app.id} className="relative rounded-xl bg-white dark:bg-gray-800/90 border border-dashed border-gray-300 dark:border-gray-600 p-4">
                        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{app.name}</h3>
                        <p className="text-xs text-gray-400 dark:text-gray-500 line-clamp-2 mt-1 mb-3">{app.description}</p>
                        <button
                          onClick={() => show(app.id)}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          Restore to dashboard
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer with Policy links */}
      <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-center text-sm text-gray-500 dark:text-gray-400">
          <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 hover:underline">
            Privacy Policy
          </Link>
          <span className="mx-2">•</span>
          <Link to="/cookie-policy" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 hover:underline">
            Cookie Policy
          </Link>
          <span className="mx-2">•</span>
          <Link to="/terms-of-service" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 hover:underline">
            Terms of Service
          </Link>
          <span className="mx-2">•</span>
          <span>&copy; {new Date().getFullYear()} JLS Trading Co. All rights reserved.</span>
        </div>
      </footer>

      <Dialog open={showPLMSDialog} onOpenChange={setShowPLMSDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>PLMS Not Set Up</DialogTitle>
            <DialogDescription>
              Your Personal Learning Management System (PLMS) has not been set up yet. Please contact your manager to get access.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end">
            <Button onClick={() => setShowPLMSDialog(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default AppHub;
